import React, { useEffect, useState } from "react";
import ProductCard from "./shop/ProductCard";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import ProductQuickView from "./shop/ProductQuickView";
import { useCart } from "@/contexts/CartContext";
import { Link } from "react-router-dom";
import { Product } from "@/types/product";
import { imageUrl } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

interface DatabaseProduct {
  id: string;
  name: string;
  image: string;
  points_price: number;
  description: string | null;
  stock: number;
  category_id: string | null;
  subcategory_id?: string | null;
  brand_id?: string | null;
  additional_images?: string[];
  price?: number;
}

const AllProducts = () => {
  const { t } = useLanguage();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const { addToCart } = useCart();
  const { ref, isVisible } = useScrollAnimation();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          brands:brand_id (name, logo),
          categories:category_id (name),
          subcategories:subcategory_id (name)
        `)
        .order('name')
        .limit(8);

      if (error) {
        throw error;
      }

      if (data) {
        // Convert database format to Product interface
        const convertedProducts: Product[] = data.map((dbProduct: DatabaseProduct) => ({
          id: dbProduct.id,
          name: dbProduct.name,
          description: dbProduct.description || '',
          image: imageUrl(dbProduct.image || "/lovable-uploads/60244da3-6deb-4628-b29a-c8e90866808e.png"),
          price: dbProduct.price || 0,
          pointsPrice: dbProduct.points_price || 0,
          brandId: dbProduct.brand_id || '',
          categoryId: dbProduct.category_id || '',
          subcategoryId: dbProduct.subcategory_id,
          stock: dbProduct.stock || 10,
          additionalImages: dbProduct.additional_images
        }));
        
        setProducts(convertedProducts);
      }
    } catch (error: any) {
      console.error("AllProducts fetch error:", error);
      toast({
        title: t('products.loadError'),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuickView = (product: Product) => {
    setSelectedProduct(product);
    setIsQuickViewOpen(true);
  };

  const handleCloseQuickView = () => {
    setIsQuickViewOpen(false);
  };

  const handleAddToCart = (product: Product) => {
    addToCart(product, 1);
    
    toast({
      description: `${product.name} ${t('home.addedToCart')}`,
    });
  };

  // Se não tiver produtos carregados ainda e ainda estiver loading, mostrar placeholders
  const displayProducts = products.length > 0 ? products : (loading ? [
    {
      id: "1",
      name: "Gaming Headset 1",
      description: "",
      image: imageUrl("/lovable-uploads/60244da3-6deb-4628-b29a-c8e90866808e.png"),
      price: 0,
      pointsPrice: 150,
      brandId: "",
      categoryId: "",
      stock: 10
    },
    {
      id: "2",
      name: "Gaming Headset 2",
      description: "",
      image: imageUrl("/lovable-uploads/60244da3-6deb-4628-b29a-c8e90866808e.png"),
      price: 0,
      pointsPrice: 200,
      brandId: "",
      categoryId: "",
      stock: 15
    },
    {
      id: "3",
      name: "Gaming Headset 3",
      description: "",
      image: imageUrl("/lovable-uploads/60244da3-6deb-4628-b29a-c8e90866808e.png"),
      price: 0,
      pointsPrice: 250,
      brandId: "",
      categoryId: "",
      stock: 5
    },
    {
      id: "4",
      name: "Gaming Headset 4",
      description: "",
      image: imageUrl("/lovable-uploads/60244da3-6deb-4628-b29a-c8e90866808e.png"),
      price: 0,
      pointsPrice: 300,
      brandId: "",
      categoryId: "",
      stock: 20
    },
  ] : []);

  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-16 bg-brand-dark">
      <div className="container mx-auto px-4">
        <h2 className={`section-heading transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>{t('home.allProductsTitle')}</h2>
        <p className={`section-subheading transition-all duration-1000 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>{t('home.exploreSelection')}</p>
        
        {loading ? (
          <div className="flex justify-center my-16">
            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-brand-red"></div>
          </div>
        ) : (
          <>
            <div className={`grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6 my-8 px-2 md:px-0 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              {displayProducts.map((product, index) => (
                <div key={product.id} className="md:hover-lift">
                  <ProductCard
                    product={product}
                    onQuickView={() => handleQuickView(product)}
                  />
                </div>
              ))}
            </div>
            
            <div className={`flex justify-center mt-10 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
              <Link to="/shop">
                <Button className="bg-brand-red hover:bg-brand-red/90 text-white hover-glow">
                  {t('home.viewMore')}
                </Button>
              </Link>
            </div>
          </>
        )}

        <ProductQuickView 
          product={selectedProduct}
          isOpen={isQuickViewOpen}
          onClose={handleCloseQuickView}
          onAddToCart={handleAddToCart}
        />
      </div>
    </section>
  );
};

export default AllProducts;
